/**
 * Cliente HTTP baseado em fetch nativo.
 *
 * Em desenvolvimento o Vite faz proxy de /api para o servidor Traccar local
 * (definido em vite.config.ts via VITE_TRACCAR_PROXY). Em produção o frontend
 * é servido pelo mesmo domínio do Traccar, então o caminho relativo /api
 * resolve direto. Em ambos os casos o cookie JSESSIONID é gerenciado pelo
 * navegador, sem token manual.
 */

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '') + '/api';

/**
 * Modo somente-leitura.
 *
 * Quando true, qualquer POST, PUT, DELETE ou PATCH é rejeitado pelo cliente
 * antes de bater no servidor, exceto para `/session` (login e logout precisam
 * funcionar). Isso é defesa em profundidade: ainda que algum bug chame um
 * endpoint de escrita, o request não sai do navegador.
 *
 * Default: true. Para destravar escritas em desenvolvimento, defina
 * `VITE_READ_ONLY=false` no .env.
 */
const READ_ONLY_MODE = import.meta.env.VITE_READ_ONLY !== 'false';
const WRITE_METHODS = new Set(['POST', 'PUT', 'DELETE', 'PATCH']);
// Allowlist de paths que podem fazer escrita mesmo em modo read-only.
// `/session` é necessário pra login/logout. `/commands/send` foi liberado em
// 2026-05-10 para permitir comandos GT06. `/geofences` (criar/editar/deletar)
// foi liberado em 2026-05-10 para o CRUD da feature de Geocercas. Qualquer
// outro write continua bloqueado.
const WRITE_ALLOWLIST = new Set(['/session', '/commands/send']);
const WRITE_ALLOWLIST_PATTERNS: RegExp[] = [
  /^\/geofences(\/\d+)?$/, // /geofences ou /geofences/{id}
  /^\/devices(\/\d+(\/accumulators)?)?$/, // /devices, /devices/{id}, /devices/{id}/accumulators
  /^\/users(\/\d+)?$/, // /users ou /users/{id}
  /^\/groups(\/\d+)?$/, // /groups ou /groups/{id}
  /^\/maintenance(\/\d+)?$/, // /maintenance ou /maintenance/{id}
  /^\/notifications(\/\d+|\/test)?$/, // /notifications, /notifications/{id}, /notifications/test
  /^\/permissions$/, // POST/DELETE /permissions (vincular/desvincular)
];

function isWritePathAllowed(path: string): boolean {
  if (WRITE_ALLOWLIST.has(path)) return true;
  return WRITE_ALLOWLIST_PATTERNS.some((re) => re.test(path));
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ReadOnlyError extends Error {
  constructor(method: string, path: string) {
    super(`Operação bloqueada em modo somente-leitura: ${method} ${path}`);
    this.name = 'ReadOnlyError';
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** valores undefined/null são omitidos; arrays viram parâmetros repetidos */
  query?: object;
  body?: unknown;
  form?: Record<string, string>;
}

function buildUrl(path: string, query?: object): string {
  const url = new URL(BASE + path, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        for (const v of value) url.searchParams.append(key, String(v));
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.pathname + url.search;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { query, body, form, headers, ...rest } = options;
  const method = (rest.method ?? 'GET').toUpperCase();

  if (READ_ONLY_MODE && WRITE_METHODS.has(method) && !isWritePathAllowed(path)) {
    throw new ReadOnlyError(method, path);
  }

  const finalHeaders = new Headers(headers);
  let finalBody: BodyInit | undefined;

  if (form) {
    finalBody = new URLSearchParams(form).toString();
    finalHeaders.set('Content-Type', 'application/x-www-form-urlencoded');
  } else if (body !== undefined) {
    finalBody = JSON.stringify(body);
    finalHeaders.set('Content-Type', 'application/json');
  }

  finalHeaders.set('Accept', 'application/json');

  const response = await fetch(buildUrl(path, query), {
    ...rest,
    headers: finalHeaders,
    body: finalBody,
    credentials: 'include',
  });

  if (response.status === 204) return undefined as T;

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new ApiError(response.status, response.statusText, text || response.statusText);
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return (await response.json()) as T;
  }
  return (await response.text()) as unknown as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'POST' }),
  put: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'PUT' }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'DELETE' }),
};
